import { usePlayTrack } from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import { ListTrackDto } from "@/lib/lutely-api/gen/dtos";
import { useLibrary } from "@/lib/lutely-api/hooks";
import { cn } from "@/lib/utils";
import { getSoundkitClipPath } from "@/lib/soundkit-clip-path";
import HeartButton from "@/components/ui/HeartButton";
import TrackArtwork from "@/components/ui/TrackArtwork";
import { Box, Button, Flex } from "@radix-ui/themes";
import { CreatorLink } from "@/components/links/CreatorLink";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { FaPlay as PlayIcon } from "react-icons/fa";
import { RiPauseFill as PauseIcon } from "react-icons/ri";

export type TrackCardProps = {
  track: ListTrackDto;
  className?: string;
  titleColor?: string;
};

export default function TrackCard({
  track,
  className,
  titleColor,
}: TrackCardProps) {
  const [trackControlsVisible, setTrackControlsVisible] = useState(false);
  const { isPlaying, playTrack, currentTrackId } = usePlayTrack(track!);
  const { isTrackSaved } = useLibrary();
  const saved = isTrackSaved(track.trackId);
  const isSoundkit = track.type === "SOUNDKIT";
  const hasPlayablePreview = Boolean(track.fileUrl);
  const trackTitle = track.title || "track";
  const playbackLabel = isPlaying
    ? `Pause ${trackTitle}`
    : `Play ${trackTitle}`;

  const showOverlay =
    hasPlayablePreview &&
    (trackControlsVisible || currentTrackId === track.trackId);

  return (
    <Box className={cn("flex flex-col items-center", className)}>
      <Box
        className={cn(
          "relative w-[160px] h-[160px] max-w-full shrink-0",
          className && "!w-full !h-auto !aspect-square",
        )}
        onMouseEnter={() => setTrackControlsVisible(true)}
        onMouseLeave={() => setTrackControlsVisible(false)}
      >
        <button
          type="button"
          onClick={hasPlayablePreview ? playTrack : undefined}
          aria-label={hasPlayablePreview ? playbackLabel : undefined}
          className={cn(
            "h-full w-full overflow-hidden border-none bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80",
            hasPlayablePreview ? "cursor-pointer" : "cursor-default",
            !isSoundkit && "rounded-lg",
          )}
        >
          <TrackArtwork
            type={track.type}
            src={track.imageUrl || "/placeholders/album_art_placeholder.png"}
            alt={`${trackTitle} cover`}
            className={cn("h-full w-full", !isSoundkit && "rounded-lg")}
          />
        </button>

        {showOverlay && (
          <Flex
            className={cn(
              "pointer-events-none absolute top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.3)] items-center justify-center",
              !isSoundkit && "rounded-lg",
            )}
            style={isSoundkit ? { clipPath: getSoundkitClipPath() } : undefined}
            aria-hidden="true"
          >
            <Button
              onClick={playTrack}
              radius="full"
              aria-label={playbackLabel}
              className="pointer-events-auto w-[54px] h-[54px] cursor-pointer"
            >
              {isPlaying ? (
                <PauseIcon className="text-[24px]" />
              ) : (
                <PlayIcon className=" text-[18px]" />
              )}
            </Button>
          </Flex>
        )}

        {(trackControlsVisible || saved) && (
          <HeartButton
            kind="track"
            id={track.trackId}
            className="absolute !m-0 top-1 right-1 z-10 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] hover:bg-black/25"
          />
        )}
      </Box>
      <Flex
        direction="column"
        className={cn("mt-[5px] min-w-0", className ? "w-full" : "w-[160px]")}
      >
        <Link
          to={`/tracks/$TRACK_ID`}
          params={{ TRACK_ID: track.trackId }}
          className={cn(
            "font-semibold text-[14px] truncate hover:underline",
            !titleColor && "text-blue-900",
          )}
          style={titleColor ? { color: titleColor } : undefined}
        >
          {track.title}
        </Link>
        <CreatorLink
          creator={track.creator}
          className="text-[#525966] font-semibold truncate text-[14px] hover:underline mt-[2px]"
        >
          {track.creator.stageName}
        </CreatorLink>
      </Flex>
    </Box>
  );
}
